import type { errori as it } from "../it/errori.ts";
import { formati } from "../formati.ts";

const f = formati("en");

/**
 * Errori che il server restituisce: li legge l'utente nell'interfaccia e chi
 * chiama le API dalla CI. Gli identificativi e i dettagli di Docker o del proxy
 * entrano come parametri, così come arrivano.
 */
export const errori: typeof it = {
  // Generici
  "errori.interno": "Internal server error",
  "errori.testo": (p: { testo: string }) => p.testo,
  "errori.autenticazioneRichiesta": "Authentication required",
  "errori.permessiInsufficienti": "Insufficient permissions",
  "errori.troppiTentativi": (p: { secondi: number }) =>
    `too many attempts: try again in ${p.secondi} ${f.plurale(p.secondi, { one: "second", other: "seconds" })}`,
  "errori.origineDiversa": "request from an origin other than Portman",
  "errori.permessoRichiesto": (p: { permesso: string }) =>
    `operation not allowed: it requires the ${p.permesso} permission`,
  "errori.endpointNonTrovato": "endpoint not found",
  "errori.datiNonValidi": (p: { problemi: string }) => `invalid data: ${p.problemi}`,
  "errori.corpoNonJson": "the request body isn't valid JSON",
  "errori.idNonValido": (p: { valore: string }) => `invalid identifier: ${p.valore}`,

  // Risorse non trovate
  "errori.stackNonTrovato": (p: { id: number | string }) => `stack ${p.id} not found`,
  "errori.revisioneNonTrovata": (p: { id: number | string }) => `revision ${p.id} not found`,
  "errori.deployNonTrovato": (p: { id: number | string }) => `deploy ${p.id} not found`,
  "errori.utenteNonTrovato": (p: { id: number | string }) => `user ${p.id} not found`,
  "errori.tokenNonTrovato": (p: { id: number | string }) => `token ${p.id} not found`,
  "errori.containerNonTrovato": (p: { id: string }) => `container ${p.id} not found`,
  "errori.cartellaNonTrovata": (p: { percorso: string }) => `folder ${p.percorso} not found`,
  "errori.progettoNonTrovato": (p: { progetto: string }) => `Compose project ${p.progetto} not found`,
  "errori.risorsaDockerNonTrovata": (p: { dettaglio: string }) => `Docker resource not found: ${p.dettaglio}`,

  // Docker e Compose
  "errori.composeAnnullato": (p: { comando: string }) =>
    `"docker compose ${p.comando}" was cancelled: whatever was already done stays as is, Compose doesn't roll back on its own.`,
  "errori.composeFallito": (p: { comando: string; codice: number | null; dettaglio: string }) =>
    `"docker compose ${p.comando}" exited with code ${p.codice}: ${p.dettaglio}`,
  "errori.dockerIrraggiungibile": (p: { socket: string }) =>
    `Docker isn't reachable at ${p.socket}. Check that the daemon is running and that Portman can access the socket.`,
  "errori.dockerRifiuta": (p: { dettaglio: string }) => `Docker rejected the request: ${p.dettaglio}`,
  "errori.logIlleggibili": (p: { dettaglio: string }) => `can't read the logs: ${p.dettaglio}`,
  "errori.eventiIlleggibili": (p: { dettaglio: string }) => `can't read the events: ${p.dettaglio}`,
  "errori.reteCollega": (p: { container: string; rete: string; dettaglio: string }) =>
    `can't connect container ${p.container} to network ${p.rete}: ${p.dettaglio}`,
  "errori.reteScollega": (p: { container: string; rete: string; dettaglio: string }) =>
    `can't disconnect container ${p.container} from network ${p.rete}: ${p.dettaglio}`,
  "errori.volumeRimozione": (p: { nome: string; dettaglio: string }) =>
    `can't remove volume ${p.nome}: ${p.dettaglio}`,
  "errori.immagineCopia": (p: { immagine: string }) =>
    `can't pull ${p.immagine} to copy the volumes`,
  "errori.copiaVolume": (p: { da: string; a: string; codice: number }) =>
    `copying ${p.da} to ${p.a} exited with code ${p.codice}`,

  // Nginx Proxy Manager
  "errori.npmIrraggiungibile": (p: { indirizzo: string }) => `Nginx Proxy Manager isn't reachable at ${p.indirizzo}.`,
  "errori.npmCredenziali":
    "Nginx Proxy Manager rejected the configured credentials. Check the email address and password in PORTMAN_NPM_IDENTITY and PORTMAN_NPM_SECRET.",
  "errori.npmToken": "Nginx Proxy Manager didn't return a valid token.",
  "errori.npmRifiuta": (p: { codice: number }) => `Nginx Proxy Manager rejected the request (code ${p.codice}).`,
  "errori.proxyStackAssente": (p: { stack: string }) => `stack "${p.stack}" no longer exists.`,
  "errori.proxyStackEsiste": (p: { stack: string }) => `stack "${p.stack}" already exists.`,
  "errori.proxyGiaConfigurato":
    "the proxy configured with the PORTMAN_NPM_* variables is already responding, or it points to an address other than the installation's.",

  // Stack
  "errori.stackNomeDuplicato": (p: { nome: string }) => `a stack named "${p.nome}" already exists`,
  "errori.stackNomeNonValido": (p: { nome: string }) =>
    `"${p.nome}" isn't a valid name: use only lowercase letters, digits, hyphens and underscores, starting with a letter or a digit. Docker Compose imposes this rule on project names.`,
  "errori.composeIlleggibile": (p: { dettaglio: string }) => `the compose file can't be read: ${p.dettaglio}`,
  "errori.stackNomeAmbiguo": (p: { nome: string; rivale: string }) =>
    `"${p.nome}" would clash with stack "${p.rivale}": blue-green deploys use the projects name-a and name-b. Pick another name.`,
  "errori.progettoEstraneo": (p: { progetto: string }) =>
    `the host already has a Compose project "${p.progetto}" that Portman doesn't manage: to bring it into Portman, import it; otherwise pick another name.`,
  "errori.revisioneAssente": (p: { stack: string; numero: number }) =>
    `stack "${p.stack}" has no revision ${p.numero}`,
  "errori.stackStessoNome": (p: { nome: string }) => `the stack is already named "${p.nome}".`,
  "errori.proxyNonRinominabile": (p: { stack: string }) =>
    `stack "${p.stack}" can't be renamed: Portman looks it up by name.`,
  "errori.stackNonAvviabile": (p: { dettaglio: string }) => `the stack can't be started: ${p.dettaglio}`,
  "errori.conflittoPorte": (p: { dettaglio: string }) => `port conflict: ${p.dettaglio}`,
  "errori.conflittoNomi": (p: { dettaglio: string }) =>
    `name conflict: ${p.dettaglio}. Names set with container_name are unique across the whole Docker daemon: remove the containers holding them, or change the names in this stack.`,
  "errori.eliminaInCorso": (p: { stack: string }) =>
    `an operation is in progress on "${p.stack}": wait for it to finish before deleting it.`,
  "errori.eliminaInEsecuzione": (p: { stack: string }) => `"${p.stack}" is running: stop it before deleting it.`,
  "errori.porteDaTogliere": "there's no host port I can remove on my own from this compose.",
  "errori.servizioFissoInstradato": (p: { servizio: string }) =>
    `service "${p.servizio}" is pinned: it has no generations to shift traffic between.`,
  "errori.portaAssegnata": (p: { porta: number; protocollo: string }) =>
    `port ${p.porta}/${p.protocollo} is already assigned to another stack`,
  "errori.volumeEsiste": (p: { volume: string }) =>
    `volume "${p.volume}" already exists: delete it or pick another name.`,
  "errori.importNomeDuplicato": (p: { nome: string }) =>
    `a stack named "${p.nome}" already exists: to import it again, delete it first or pick another name.`,

  // Rilascio
  "errori.reteNonPronta": (p: { dettaglio: string; stato: string }) =>
    `${p.dettaglio}. The container state is "${p.stato}": if it isn't running steadily, look there before the network.`,
  "errori.nonPronto": (p: { container: string; secondi: number; motivo: string }) =>
    `${p.container} didn't become ready after ${p.secondi} s: ${p.motivo}. Traffic wasn't shifted: the previous version is still the active one.`,
  "errori.serveProxy":
    "zero-downtime deploys need a proxy. Install it from the Deploy tab, or set PORTMAN_NPM_URL, PORTMAN_NPM_IDENTITY and PORTMAN_NPM_SECRET.",
  "errori.senzaDominio": (p: { stack: string }) =>
    `stack "${p.stack}" has no domain configured: without a route, traffic can't be shifted from one version to the other.`,

  // Utenti e accesso
  "errori.passwordDebole": (p: { problemi: string }) => `the password isn't strong enough: ${p.problemi}`,
  "errori.giaConfigurato": "Portman is already set up: the first administrator exists",
  "errori.tokenSetup": "Invalid setup token: you'll find it in Portman's startup log",
  "errori.credenziali": "Invalid credentials",
  "errori.passwordAttuale": "the current password is incorrect",
  "errori.usernameDuplicato": (p: { username: string }) => `a user with username "${p.username}" already exists`,
  "errori.usernameVuoto": "the username can't be empty",
  "errori.ultimoAdmin":
    "operation refused: this is the last active administrator, and without one nobody could administer Portman anymore. Make someone else an administrator first.",
  "errori.eliminaSeStessi": "you can't delete yourself",

  // Backup
  "errori.backupNonSqlite": "the uploaded file isn't a readable SQLite database",
  "errori.backupDanneggiato": "the uploaded database is corrupted and can't be restored",
  "errori.backupNonPortman": "the uploaded database isn't a Portman backup: the migrations table is missing",
  "errori.backupSenzaUtenti":
    "the backup contains no users: restoring it would lock everyone out of Portman",
  "errori.backupVuoto": "the uploaded file is empty",

  // Cartelle
  "errori.cartellaNonVuota": (p: { percorso: string }) => `folder ${p.percorso} isn't empty`,
  "errori.cartellaPermesso": (p: { percorso: string }) =>
    `permission denied on ${p.percorso}: Portman's uid 1000 must be able to write there`,
  "errori.cartelleSpente":
    "folder management is off: set PORTMAN_STORAGE_ROOT and mount that folder in the container",
  "errori.percorsoFuoriRadice": (p: { percorso: string; radice: string }) =>
    `path "${p.percorso}" goes outside the root ${p.radice}`,
  "errori.cartellaNome": "enter the folder name",
  "errori.cartellaEsiste": (p: { percorso: string }) => `folder ${p.percorso} already exists`,
  "errori.radiceNonEliminabile": "the root can't be deleted",

  // Email
  "errori.linguaDaAmbiente": "the language is set by PORTMAN_LANG: to change it, edit the variable and restart Portman",
  "errori.provaEmailIncompleta": "sending a test needs the SMTP server and at least one recipient",
  "errori.invioEmail": (p: { dettaglio: string }) => `sending failed: ${p.dettaglio}`,

  // Token di lettura e registry
  "errori.tokenNome": (p: { max: number }) => `the name must be 1 to ${p.max} characters long`,
  "errori.tokenDurata": (p: { ammesse: string }) => `invalid duration: ${p.ammesse}`,
  "errori.tokenStack": "choose at least one stack",
  "errori.stackInesistente": (p: { id: number }) => `no stack with identifier ${p.id}`,
  "errori.registryIndirizzo": "enter the registry address",
  "errori.registryUtente": "enter the registry user",
  "errori.registryPassword": "enter the registry password",
  "errori.registryInesistente": (p: { id: number }) => `no registry with identifier ${p.id}`,

  // Validazione dei campi (le altre frasi di Zod arrivano dalla sua traduzione)
  "validazione.corpo": "invalid body",
  "validazione.porteProxyUguali": "the HTTP and HTTPS ports must be different",
  "validazione.finestraInvertita": "from must come before to",
  "validazione.finestraTroppoLunga": "the time window can't exceed 7 days",
  "validazione.nessunCampo": "specify at least one field to change",
  "validazione.nomeVariabile": "invalid variable name",
  "validazione.nomeVariabileDettaglio": "invalid variable name (letters, digits and underscores)",
  "validazione.descrizioneLunga": "the description exceeds 500 characters",

  // Webhook
  "errori.webhookToken": "invalid webhook token",
  "errori.webhookSenzaTag": 'the call must specify the tag to deploy, for example {"tag":"v1.2.3"}',
  "errori.webhookTagNonValido": (p: { tag: string }) =>
    `"${p.tag}" isn't a valid tag: letters, digits, dots, hyphens and underscores are allowed.`,
  "errori.tagNonUsato": (p: { variabile: string }) =>
    `no image in the compose uses \${${p.variabile}}: write for example image: app:\${${p.variabile}}`,
  "errori.ipBloccato": "access denied: this address is blocked",
  "errori.ipTuo": "you can't block the address you are connected from",
  "validazione.ip": "invalid IP address",
};
